import {
  ApolloClient,
  createHttpLink,
  type FetchResult,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { refreshAccessToken } from '@dashboard/auth/restClient';
import { clearTokens, getAccessToken } from '@dashboard/auth/storage';

import { type TypedTypePolicies } from './apollo-helpers';

/**
 * The dashboard's Apollo client now piggy-backs on the REST auth surface.
 *
 * - Access tokens are read from the in-memory store managed by the new
 *   ``@dashboard/auth/storage`` module (no more SaleorClient bootstrap).
 * - When the GraphQL backend reports an expired token we delegate to the
 *   REST refresh helper, which is single-flight across all callers (the
 *   axios interceptor + this fetch wrapper share the same in-flight
 *   promise).
 * - Successful refreshes replay the original GraphQL request once.  A
 *   failed refresh clears tokens and lets the request fall through so the
 *   caller (Apollo / a query hook) sees the original ``ExpiredSignatureError``.
 */

let client: ApolloClient<NormalizedCacheObject>;

const JWT_ERROR_CODES = new Set(['ExpiredSignatureError', 'JWT_INVALID_TOKEN']);

export type FetchConfig = Partial<{
  /**
   * Refresh token and retry the request when the backend responds with a
   * JWT-related error.  Defaults to ``true``.
   */
  refreshOnUnauthorized: boolean;
}>;

const headersHaveAuth = (headers: HeadersInit | undefined): boolean => {
  if (!headers) return false;

  if (headers instanceof Headers) {
    return headers.has('authorization');
  }

  if (Array.isArray(headers)) {
    return headers.some(([k]) => k.toLowerCase() === 'authorization');
  }

  return Object.keys(headers).some((k) => k.toLowerCase() === 'authorization');
};

const setAuthHeader = (init: RequestInit, token: string): RequestInit => ({
  ...init,
  headers: {
    ...init.headers,
    Authorization: `Bearer ${token}`,
  },
});

export const createFetch =
  ({ refreshOnUnauthorized = true }: FetchConfig = {}) =>
  async (input: RequestInfo, init: RequestInit = {}): Promise<Response> => {
    let token = getAccessToken();
    let request = init;

    if (token && !headersHaveAuth(init.headers)) {
      request = setAuthHeader(init, token);
    }

    const response = await fetch(input, request);

    if (!refreshOnUnauthorized || !token) {
      return response;
    }

    let payload: FetchResult | null = null;

    try {
      payload = await response.clone().json();
    } catch {
      return response;
    }

    const isUnauthenticated = payload?.errors?.some((error) => {
      const code = (error.extensions?.exception as Record<string, unknown> | undefined)?.code;

      return typeof code === 'string' && JWT_ERROR_CODES.has(code);
    });

    if (!isUnauthenticated) {
      return response;
    }

    const refreshed = await refreshAccessToken();

    if (!refreshed) {
      clearTokens();

      return response;
    }

    token = refreshed;

    return fetch(input, setAuthHeader(init, token));
  };

const getTypePolicies = (): TypedTypePolicies => ({
  User: {
    /**
     * IMPORTANT
     * This works as long as we have 1 User cache object which is the current logged in User.
     * If the client should ever fetch additional Users, this should be removed
     * and the login methods (token create or verify) should be responsible for writing USER query cache manually.
     */
    keyFields: [],
    fields: {
      addresses: {
        merge: false,
      },
    },
  },
});

export const createApolloClient = (
  apiUrl: string,
  _autologin: boolean,
  fetchOptions?: FetchConfig,
): ApolloClient<NormalizedCacheObject> => {
  const httpLink = createHttpLink({
    fetch: createFetch(fetchOptions),
    uri: apiUrl,
    credentials: 'include',
  });

  const cache = new InMemoryCache({
    typePolicies: getTypePolicies(),
  });

  client = new ApolloClient({
    cache,
    link: httpLink,
  });

  return client;
};
